import { Box, IconButton, SvgIcon, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import { Section } from '../../components/ui/Section';
import { SyntheticEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTabPanel } from '../Reporting/components/CustomTabPanel';
import { ReportNavItem, ReportTypesData } from './data/ReportTypesDataV2';
import { ReportTabType } from '../../enums/ReportTabType';
import ReportHistoryTab, { ReportHistoryTabHandle } from '../Reporting/components/ReportHistoryTab';
import UserReportsTab from '../Reporting/components/UserReportsTab';
import { UserContext } from '../../components/shared/useUser';
import useNswagClient from '../../hooks/api/useNswagClient';
import { ConfigurationName } from '../../enums/ConfigurationName';
import { ReportDownloadDetail } from '../Reporting/interfaces/ReportDownloadDetail';
import { prepareReports } from '../Reporting/helpers/ReportingHelper';
import { ReportType } from '../../enums/ReportType';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { RefreshCw05 } from '../../assets';
import RecentReportsPanel from './components/RecentReportsPanel';
import StoreScopePicker, { StoreScope } from './components/StoreScopePicker';
import ScopeChips from './components/ScopeChips';
import GenerateReportForm from './components/GenerateReportForm';

/**
 * Reports V2 generation + history screen. Route access is controlled by ConfigurationPreviewRoute.
 *
 * Status note: report status is refreshed manually (top-right refresh) and after each generation —
 * there is intentionally no polling timer, matching the legacy page's refresh behaviour.
 */
const ReportingV2 = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { user, selectedStore, selectedFranchise, hasPermissionTo, isAdmin } = useContext(UserContext);
  const { getConfiguration, getReportsAvailableForDownload } = useNswagClient();

  const [tab, setTab] = useState(0);
  const [storeScope, setStoreScope] = useState<StoreScope | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportNavItem | null>(null);
  const [filteredReports, setFilteredReports] = useState<ReportNavItem[]>([...ReportTypesData]);
  const [reports, setReports] = useState<ReportDownloadDetail[]>([]);
  const [isRefreshingReports, setIsRefreshingReports] = useState(false);
  const reportHistoryTabRef = useRef<ReportHistoryTabHandle>(null);

  // Mirrors the legacy Reporting page guards (pages/Reporting/index.tsx) so V2 offers exactly the
  // same reports per user. Franchises where recipe details require an explicit permission, and
  // the Chick-fil-A franchises that get the CFA-only reports.
  const franchisesThatRequirePermission = ['NE Apple Green GB', 'Chick-fil-A Singapore', 'Chick-fil-A UK'];
  const canViewRecipeDetails = hasPermissionTo(['RecipeDetailsRead']) ||
    !franchisesThatRequirePermission.includes(selectedStore?.franchiseName || '');
  const showOnlyForCFA = franchisesThatRequirePermission.includes(selectedStore?.franchiseName || '');

  // getReportsAvailableForDownload is recreated every render by useNswagClient, so depend only on a
  // stable primitive to avoid an effect loop; the client is read fresh inside via a ref.
  const getReportsRef = useRef(getReportsAvailableForDownload);
  getReportsRef.current = getReportsAvailableForDownload;

  const refreshReports = useCallback(() => {
    return getReportsRef.current()
      .then((response) => {
        if (response?.data?.reports) {
          setReports(prepareReports(response.data.reports));
        }
      })
      .catch(() => {
        // Non-fatal: status panels simply stay on their last known state.
      });
  }, []);

  // Seed the default scope (current franchise, all its stores). The scope picker lives inside a
  // collapsed Advanced filters section and doesn't mount until expanded, so the page must set the
  // default itself — otherwise scoped forms never render (they wait for a scope). Re-seeds when the
  // global tenant/franchise context changes while the page is mounted; a user-cleared scope is left
  // alone because the seeded context key is unchanged.
  const seededContextRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user) {
      return;
    }
    const franchiseId = selectedFranchise?.franchiseId ?? selectedStore?.franchiseId;
    const tenantId =
      selectedFranchise?.tenantId ??
      user.franchises?.find((f) => f.franchiseId === franchiseId)?.tenantId;
    if (franchiseId == null || tenantId == null) {
      return;
    }
    const contextKey = `${tenantId}:${franchiseId}`;
    if (seededContextRef.current === contextKey) {
      return;
    }
    seededContextRef.current = contextKey;
    setStoreScope({ tenantId, franchiseId, storeNumbers: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedFranchise, selectedStore]);

  useEffect(() => {
    refreshReports();
  }, [refreshReports]);

  useEffect(() => {
    if (!selectedStore) {
      return;
    }

    let reportsForUser = ReportTypesData.filter((x) => hasPermissionTo([x.permission]));

    if (!isAdmin() && selectedStore?.franchiseName !== 'EG ON THE MOVE LTD') {
      reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.StockCountSummary);
    }
    if (!hasPermissionTo(['MadeInStoreRead'])) {
      reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.MadeInStore);
    }
    if (!selectedStore?.franchiseName?.toLowerCase().includes('amrest')) {
      reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.ItemUsage);
    }
    if (!canViewRecipeDetails) {
      reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.Margin);
    }
    if (!showOnlyForCFA) {
      reportsForUser = reportsForUser.filter(
        (x) => x.title !== ReportType.inventoryExtension && x.title !== ReportType.giveawaysSamples,
      );
    }
    if (!hasPermissionTo(['ReportUserRead'])) {
      reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.UserReport);
    }

    getConfiguration(ConfigurationName.COG, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        if (res?.data?.value !== 'true') {
          reportsForUser = reportsForUser.filter((x) => x.title !== ReportType.Cogs);
        }
        setFilteredReports([...reportsForUser]);
      })
      .catch(() => {
        setFilteredReports([...reportsForUser]);
      });
    // Deps intentionally limited to store identity — hasPermissionTo/isAdmin/getConfiguration are
    // recreated every render by their hooks and would otherwise loop this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedStore]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleRefresh = () => {
    setIsRefreshingReports(true);
    Promise.resolve(reportHistoryTabRef.current?.refresh())
      .catch(() => {
        // ignore
      })
      .finally(() => {
        refreshReports().finally(() => setIsRefreshingReports(false));
      });
  };

  // Fired by every report form after a successful enqueue. Refresh immediately, then once more
  // after a short delay — TaskRunner needs a beat to write the queued row, and the first fetch
  // can otherwise come back before the new report exists.
  const handleReportGenerated = () => {
    refreshReports();
    reportHistoryTabRef.current?.refresh();
    setTimeout(() => {
      refreshReports();
      reportHistoryTabRef.current?.refresh();
    }, 2000);
  };

  return (
    <PageArea>
      <Box
        sx={{
          textAlign: 'left',
          color: 'black',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          alignSelf: 'stretch',
        }}>
        <PageTitle>{t('reporting.title')}</PageTitle>
        {/* The Reports tab renders its own refresh inside the table filter row; the header
            button only serves the User Reports tab now. */}
        {tab === ReportTabType.UserReports && (
          <Tooltip title={t('reporting.refresh')}>
            <span>
              <IconButton onClick={handleRefresh}
                disabled={isRefreshingReports}
                aria-label={t('reporting.refresh')}>
                <SvgIcon sx={isRefreshingReports ? { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } } : undefined}>
                  <RefreshCw05 />
                </SvgIcon>
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ py: '12px', color: 'black', fontWeight: 'bold' }}>
        <Tabs aria-label="Report tabs"
          value={tab}
          onChange={handleTabChange}>
          <Tab label={t('reporting.generate-report')} />
          <Tab label={t('reporting.reports')} />
          {isAdmin() && <Tab label={t('reporting.user-reports')} />}
        </Tabs>
      </Box>

      <Box sx={{
        display: 'flex', flexDirection: 'column', width: '100%', height: '100%',
      }}>
        <CustomTabPanel value={tab}
          index={ReportTabType.GenerateReport}>
          <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap', pt: '4px' }}>
            <Box sx={{ flex: 1, minWidth: '340px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Section>
                <Box sx={{
                  px: '20px',
                  py: '14px',
                  borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
                }}>
                  <Typography variant="textLG"
                    sx={{ fontWeight: 600 }}>
                    {t('reportingV2.report-details')}
                  </Typography>
                </Box>
                <Box sx={{ p: '20px' }}>
                  <GenerateReportForm
                    reports={filteredReports}
                    storeScope={storeScope}
                    onReportChange={setSelectedReport}
                    onGenerated={handleReportGenerated}
                    scopeContent={
                      // Scope shortcuts (current store / current franchise) sit directly under the
                      // report description, alongside the quick date chips. Hidden for the User
                      // report, which is franchise-scoped and has its own multi-franchise picker.
                      selectedReport?.title !== ReportType.UserReport
                        ? (
                          <Box>
                            <Typography variant="textMD"
                              sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
                              {t('reportingV2.scope')}
                            </Typography>
                            <ScopeChips scope={storeScope}
                              onSelect={setStoreScope} />
                          </Box>
                        )
                        : undefined
                    }
                    scopeAdvancedContent={
                      // Full tenant/franchise/store pickers, rendered bare after the form until each
                      // form is converted to own its Advanced collapse. Hidden for the User report.
                      selectedReport?.title !== ReportType.UserReport
                        ? <StoreScopePicker onChange={setStoreScope}
                          value={storeScope} />
                        : undefined
                    }
                  />
                </Box>
              </Section>
            </Box>
            <RecentReportsPanel reports={reports}
              onRefresh={handleRefresh}
              refreshing={isRefreshingReports} />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={tab}
          index={ReportTabType.ReportHistory}>
          <ReportHistoryTab ref={reportHistoryTabRef}
            onLoadingChange={setIsRefreshingReports}
            variant="table"
            onRefresh={handleRefresh}
            isRefreshing={isRefreshingReports}
            canDelete={hasPermissionTo(['ReportDelete'])} />
        </CustomTabPanel>
        <CustomTabPanel value={tab}
          index={ReportTabType.UserReports}>
          <UserReportsTab />
        </CustomTabPanel>
      </Box>
    </PageArea>
  );
};

export default ReportingV2;
